import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useModel } from '../ModelProvider';
import { r2 } from '../engines/computeBS';
import { injectDynamicBSLines } from '../engines/dynamicLines';
import { groupByYear, getYear, formatMonthLabel } from '../engines/dateUtils';
import { quarterColumns } from '../utils/quarterColumns';
import { yearLabel } from '../utils/yearColumns';   // a partial year says so: "2026 (Oct-Dec)" (2026-11-02)
import { updateBSAssumption } from '../api';
import OnboardingIncomplete from '../components/OnboardingIncomplete';
import ModelLoadError from '../components/ModelLoadError';
import ViewOnly, { ViewOnlyNote } from '../components/ViewOnly';

const fmtK = n => {
  if (n === 0) return "—";
  const s = Math.abs(n) >= 1000 ? Math.abs(n).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2}) : Math.abs(n).toFixed(2);
  return n < 0 ? `(${s})` : s;
};

// Tenant-agnostic assumption grouping. Replaces the former hardcoded
// ASSUM_GROUPS line-id list (Semitek-shaped → stranded any non-Semitek line,
// e.g. cid=3 brex_credit_card / wells_fargo_1020_credit_card / other_receivables).
// Each assumption row now groups under the title its bsLines-leaf `subcategory`
// maps to here (subcategory vocabulary is identical across tenants); groups
// render in ASSUM_GROUP_ORDER; rows order by BS chart position.
const ASSUM_GROUP_ORDER = [
  "Cash (CFS/Sweep)", "Receivables", "Other Current Assets", "Fixed Assets",
  "Intangible Assets", "Payables", "Other Current Liabilities",
  "Deferred Revenue", "Long Term Liabilities", "Equity", "Other",
];
const SUBCAT_TO_GROUP = {
  sweep:"Cash (CFS/Sweep)", clearing:"Cash (CFS/Sweep)",
  accounts_receivable:"Receivables",
  prepaid:"Other Current Assets", other_receivable:"Other Current Assets", other_current_asset:"Other Current Assets",
  fixed_assets:"Fixed Assets",
  intangible_assets:"Intangible Assets",
  accounts_payable:"Payables", credit_card:"Payables",
  payroll:"Other Current Liabilities", accrued:"Other Current Liabilities", tax:"Other Current Liabilities",
  short_term_debt:"Other Current Liabilities",
  deferred_revenue:"Deferred Revenue",
  intercompany:"Long Term Liabilities", shareholder_loan:"Long Term Liabilities", debt:"Long Term Liabilities",
  safe:"Equity", preferred_stock:"Equity", common_stock:"Equity", issuance_costs:"Equity", treasury:"Equity", retained_earnings:"Equity",
};
// Collapse set is keyed on DRIVER-TYPE (not a line-list): engine-driven /
// derived lines hide behind the "Show derived/locked lines" toggle by default.
const LOCKED_DRIVERS = new Set(["module_link","formula"]);
// Cash / sweep accounts zero out in the forecast (sweep flexes total_clearing to
// minCash; the rest sweep into the target) — they are sweep-driven, NOT operator-set.
// Locked on top of the driver-based lock above, so they don't render as settable
// "constant" rows.
//
// Locked by ROLE (the leaf's semantic classification) as well as by the legacy
// SUBCATEGORY — a strict UNION, so current tenants (whose cash leaves carry both) are
// unchanged, while a tenant classified role='cash' without subcategory='clearing'
// still gets locked. Without the role half, an operator could set a DSO driver on a
// bank account and silently break the sweep.
const LOCKED_SUBCATS = new Set(["clearing","sweep"]);
const LOCKED_ROLES = new Set(["cash","clearing","sweep_target"]);

const FONT="'DM Sans',-apple-system,sans-serif";const BLUE="#2A6DB0";const BLACK="#1A1C2E";
const SL={position:"sticky",left:0,zIndex:5,background:"inherit"};
const DRIVER_OPTS=[{v:"dso",l:"DSO (Rev x Days / 30)"},{v:"dpo",l:"DPO (Exp x Days / 30)"},{v:"constant",l:"Constant (carry fwd)"},{v:"assumption",l:"Assumption (manual)"}];


export default function BalanceSheetPage() {
  const {
    bsData: data, cutoff, loading, ensureFresh,
    bsAssumptions: assumptions, setBsAssumptions: setAssumptions, canEditModel,
    isData, rawData, frLines, implDeferredLines, companyId, onboardingGap,
    modelState, modelError,
  } = useModel();
  // Phase 3b: prefer per-company bsLines from ModelProvider; fall back to
  // ★ SWEEP B4b (A8): was `|| BS_LINES` — Semitek's chart rendered as this tenant's
  // balance sheet. Honest-empty instead; the engine returns no data for a chartless
  // tenant, so an empty line list renders an empty statement rather than a foreign one.
  const baseBsLines = rawData.bsLines || [];

  // Phase 6: Base IS Line dropdown options for DSO/DPO drivers. Combines
  // engine-computed aggregates (always available via orchestrator.buildBSIsRef)
  // with per-line revenue lines from the tenant's IS. base_is_line_id can
  // point to any of these and the engine looks it up in IS_REF.
  const baseLineOptions = useMemo(() => {
    const opts = [
      { id: 'total_revenue',  label: 'Total Revenue (all)' },
      { id: 'total_cogs',     label: 'Total COGS' },
      { id: 'total_sga',      label: 'Total SG&A' },
      { id: 'total_cogs_sga', label: 'Total COGS + SG&A' },
      { id: 'vendor_opex',    label: 'Vendor OpEx (vendor engine sum)' },
    ];
    const seen = new Set(opts.map(o => o.id));
    const lines = rawData.isLines || [];
    lines.filter(l => l.role === 'revenue_line').forEach(l => {
      if (!seen.has(l.id)) { opts.push({ id: l.id, label: l.label + ' (revenue line)' }); seen.add(l.id); }
    });
    return opts;
  }, [rawData.isLines]);
  const A = rawData.bsActuals || {};
  const H = rawData.bsForecast || {};
  const allMonths = useMemo(() => rawData.allMonths || [], [rawData.allMonths]);
  const companyName = rawData.companyName || '';
  const IS_REF_REVENUE = isData.total_revenue || {};
  const IS_REF_COGS_SGA = {};
  allMonths.forEach(m => { IS_REF_COGS_SGA[m] = r2((isData.total_cogs?.[m]||0)+(isData.total_sga?.[m]||0)); });
  const VENDOR_OPEX = rawData.vendorOpex || {};

  const [collapsed, setCollapsed] = useState(new Set(["clearing_header"]));
  const [viewMode, setViewMode] = useState("monthly");
  /* ★ G4 — a deep link from Step 9 opens the assumptions panel on arrival. Same shape as
   * RevenueBuildPage / DeferredRevenuePage (f7cfd35): read ONCE at mount from the query
   * string, because this is an entry-point concern rather than reactive state, and a lazy
   * initialiser avoids adding a router hook to a page that imports none.
   * ⛔ THE DEFAULT IS UNCHANGED. Only the exact value "assumptions" opens it; anything
   * else — absent, unknown, hand-edited — falls back to collapsed, exactly as today. The
   * panel is a PANEL, not a tab, so `?tab=` is stretched slightly rather than a second
   * convention invented for one link.
   * ⓘ DEFAULT-OPEN-WHEN-UNCONFIGURED WAS CONSIDERED AND REFUSED: bs_assumptions has no
   * created_at and no provenance column, and the Step-4 seed writes updated_at=NOW()
   * exactly as an operator edit does, so "has this tenant ever touched its assumptions?"
   * is not answerable from the data. Inferring it from timestamp clustering would be a
   * "looks default" guess — the §E1 failure. Measurement: diffs/ux-build3.txt §2. */
  const [showPanel, setShowPanel] = useState(
    () => new URLSearchParams(window.location.search).get("tab") === "assumptions");
  useEffect(() => { ensureFresh(); }, [ensureFresh]);

  // Sub-phase 4 (page sweep): yearBands derived from groupByYear(allMonths) — drives both
  // the columns useMemo annual path AND the thead year-band row. Replaces hardcoded
  // [2024,2025,2026] iteration; mirrors DeferredRevenuePage 3.4b pattern.
  const yearBands = useMemo(() => {
    const groups = groupByYear(allMonths);
    return Object.keys(groups).sort().map(y => ({ year: +y, months: groups[y] }));
  }, [allMonths]);
  const lastYear = yearBands.length ? yearBands[yearBands.length - 1].year : null;
  const columns=useMemo(()=>{
    if(viewMode==="monthly")return allMonths.map(m=>({key:m,label:formatMonthLabel(m, "short"),months:[m],year:getYear(m)}));
    if(viewMode==="quarterly")return quarterColumns(allMonths,{label:"short"});
    return yearBands.map(b=>({key:String(b.year),label:yearLabel(b.year,b.months),months:b.months,year:b.year}));
  },[viewMode, allMonths, yearBands]);

  const bsVal=(id,months)=>{if(!data[id])return 0;return r2(data[id]?.[months[months.length-1]]||0);};
  const colIsFc=c=>c.months.every(m=>m>cutoff);const colIsAc=c=>c.months.every(m=>m<=cutoff);
  const colColor=c=>colIsFc(c)?BLACK:colIsAc(c)?BLUE:"#5B4B8A";
  const isYearEnd=c=>c.months[c.months.length-1].endsWith("-12")&&c.year!==lastYear;
  const toggle=id=>setCollapsed(p=>{const n=new Set(p);n.has(id)?n.delete(id):n.add(id);return n;});

  // ★ A1 — the runtime rows come from THE ONE injector the ENGINE calls (computeBS), so the
  // statement rendered here and the statement computed by the model are the same skeleton.
  // The parallel copy that stood here is DELETED. It differed in three ways, all invisible
  // because fundraising_rounds has never had a row: it `.reverse()`d the rounds into
  // oldest-first (the engine emits newest-first, which is what the API's ORDER BY … DESC and
  // CLAUDE.md §7 both specify); its re-grouping was a no-op that existed only to feed that
  // reverse; and the grouping matched rounds by SUBSTRING (`bsId.includes('7_series_b')` is
  // true for round 17), which would have emitted a colliding round's rows twice. Its two
  // `sumOf` writes were dead — this page reads values by leaf id from the engine's output.
  const lines = useMemo(
    () => injectDynamicBSLines(baseBsLines, frLines, implDeferredLines),
    [baseBsLines, frLines, implDeferredLines]
  );

  const{sections,totalToHeader}=useMemo(()=>{
    const pairs=[],hs=[];
    lines.forEach((l,i)=>{if(l.type==="header")hs.push(i);else if(l.type==="total"){for(let s=hs.length-1;s>=0;s--){if(lines[hs[s]].level<=l.level){pairs.push({hId:lines[hs[s]].id,hIdx:hs[s],tId:l.id,tIdx:i});hs.splice(s,1);break;}}}});
    const secs={},t2h={};
    pairs.forEach(p=>{const ch=[];for(let i=p.hIdx+1;i<p.tIdx;i++)ch.push(lines[i].id);secs[p.hId]={totalId:p.tId,children:ch};t2h[p.tId]=p.hId;});
    return{sections:secs,totalToHeader:t2h};
  },[lines]);

  const isHidden=lineId=>{
    for(const[hId,sec]of Object.entries(sections)){
      if(!collapsed.has(hId)||!sec.children.includes(lineId))continue;
      let sh=false;
      for(const[subH,subS]of Object.entries(sections)){
        if(subH===hId||!sec.children.includes(subH))continue;
        if(collapsed.has(subH)){if(lineId===subS.totalId){sh=true;break;}}
        else{if(subS.children.includes(lineId)||lineId===subS.totalId){sh=true;break;}}
      }
      if(!sh)return true;
    }return false;
  };

  const arrow=d=><span style={{display:"inline-block",width:14,fontSize:9,color:"#8B8FA3",marginRight:4}}>{d?"\u25B6":"\u25BC"}</span>;

  // Phase 6: each in-panel edit updates React state (live recompute via
  // handleSetBsAssumptions -> runOrchestrator) AND persists to the
  // bs_assumptions DB table. A refused save does not roll back the local change -- the row keeps the value and
  // SAYS it was not saved (W-029, 2026-09-30; it used to reach only the console), so the operator can retry.
  // saveSeq numbers each row's requests so only the LATEST one's outcome is shown. The message is set only from the
  // promise (never inside the state updater persist is called from).
  const [saveErr, setSaveErr] = useState({});
  const saveSeq = useRef({});
  const persist = useCallback((id, nextEntry) => {
    if (!companyId) return;
    const updates = {
      driver: nextEntry.driver,
      days: nextEntry.days != null ? nextEntry.days : null,
      base_is_line_id: nextEntry.base_is_line_id || null,
      formula: nextEntry.formula || null,
      fz: nextEntry.fz === true,
      constantValue: nextEntry.constantValue != null ? nextEntry.constantValue : null,
      values: nextEntry.values || {},
    };
    const seq = (saveSeq.current[id] || 0) + 1;
    saveSeq.current[id] = seq;
    updateBSAssumption(companyId, id, updates).then(() => {
      if (saveSeq.current[id] !== seq) return;
      setSaveErr(p => { if (!p[id]) return p; const n = { ...p }; delete n[id]; return n; });
    }).catch(err => {
      console.error('Failed to persist bs_assumption', id, err);
      if (saveSeq.current[id] !== seq) return;
      const msg = String(err?.response?.data?.error || err?.message || 'Save failed.');
      setSaveErr(p => ({ ...p, [id]: 'Not saved \u2014 ' + msg }));
    });
  }, [companyId]);

  const updateDriver = (id, nv) => setAssumptions(p => {
    const n = JSON.parse(JSON.stringify(p));
    n[id].driver = nv;
    // Phase 6: base_is_line_id replaces the old "base" sentinel field.
    // Defaults preserve pre-Phase-6 engine behavior (DSO→total_revenue,
    // DPO→vendor_opex; the legacy "cogs_sga" label was misleading — engine
    // actually divided by vendor_opex).
    if (nv === "dso") {
      n[id].days = n[id].days || 0;
      n[id].base_is_line_id = n[id].base_is_line_id || 'total_revenue';
      delete n[id].base; // strip legacy field
    } else if (nv === "dpo") {
      n[id].days = n[id].days || 0;
      n[id].base_is_line_id = n[id].base_is_line_id || 'vendor_opex';
      delete n[id].base;
    } else if (nv === "assumption" || nv === "module_link") {
      if (!n[id].values) n[id].values = {};
    }
    persist(id, n[id]);
    return n;
  });
  const updateDays = (id, v) => setAssumptions(p => {
    const n = JSON.parse(JSON.stringify(p));
    const d = parseFloat(v);
    if (!Number.isFinite(d)) return p; // W-029: never store a blank as 0 (callers pass a number)
    n[id].days = d;
    persist(id, n[id]);
    return n;
  });
  /* W-029: the typed day count. A BLANK is refused -- nothing is sent until a number is entered -- because a blank
     used to be stored as 0 days, which silently empties the balance. An explicit 0 is a real answer and is sent. */
  const [daysRequired, setDaysRequired] = useState({});
  const commitDays = (id, v) => {
    const t = String(v == null ? '' : v).trim();
    if (t === '' || !Number.isFinite(parseFloat(t))) { setDaysRequired(p => ({ ...p, [id]: true })); return; }
    setDaysRequired(p => { if (!p[id]) return p; const n = { ...p }; delete n[id]; return n; });
    updateDays(id, t);
  };
  const updateBase = (id, v) => setAssumptions(p => {
    const n = JSON.parse(JSON.stringify(p));
    n[id].base_is_line_id = v;
    delete n[id].base;
    persist(id, n[id]);
    return n;
  });
  /* W-029: a BLANK month REMOVES that month's override -- the engine (computeBS) reads a missing month as "use the
     default", exactly what the field shows before anything is typed. It used to be stored as 0. */
  const updateAssumValue = (id, m, v) => setAssumptions(p => {
    const n = JSON.parse(JSON.stringify(p));
    if (!n[id].values) n[id].values = {};
    const t = String(v == null ? '' : v).trim();
    const x = parseFloat(t);
    if (t === '' || !Number.isFinite(x)) {
      if (n[id].values[m] === undefined) return p;
      delete n[id].values[m];
    } else {
      n[id].values[m] = x;
    }
    persist(id, n[id]);
    return n;
  });
  const applyToAll = (id, v) => setAssumptions(p => {
    const t = String(v == null ? '' : v).trim();
    const x = parseFloat(t);
    if (t === '' || !Number.isFinite(x)) return p; // W-029: a blank "Apply to all" applies nothing
    const n = JSON.parse(JSON.stringify(p));
    if (!n[id].values) n[id].values = {};
    const fMonths = allMonths.filter(m => m > cutoff);
    fMonths.forEach(m => { n[id].values[m] = x; });
    persist(id, n[id]);
    return n;
  });
  const [expandedAssum, setExpandedAssum] = useState(new Set());
  const [showLocked, setShowLocked] = useState(false);
  const toggleAssum = id => setExpandedAssum(p=>{const n=new Set(p);n.has(id)?n.delete(id):n.add(id);return n;});
  // Smart format for days: whole numbers unless all values < 1 day
  const fmtDays = useCallback((d, maxVal) => {
    if (maxVal < 1) return d < 0.01 ? d.toFixed(4) : d < 0.1 ? d.toFixed(3) : d.toFixed(2);
    return Math.round(d).toString();
  },[]);
  const forecastMonths = useMemo(()=>allMonths.filter(m=>m>cutoff),[allMonths, cutoff]);
  const actualsMonths = useMemo(()=>allMonths.filter(m=>m<=cutoff),[allMonths, cutoff]);

  // Tenant-dynamic assumption grouping. Buckets every bs_assumptions row into
  // the tenant-agnostic group titles using each line's bsLines-leaf
  // `subcategory` (same source the BS table renders), ordered by BS chart
  // position. Replaces the hardcoded ASSUM_GROUPS so no tenant's lines are
  // stranded. total_clearing (synthetic subtotal, no meta) → Cash; any
  // deferred_rev* synthetic not present as a leaf → Deferred Revenue.
  // Returns: assumGroups (ordered group buckets), lockedAssumIds (engine/sweep
  // -driven rows hidden behind the toggle + rendered label-only), labelById
  // (line NAME for each row, sourced from the bsLines leaf — the GET map has
  // no label field).
  const { assumGroups, lockedAssumIds, labelById } = useMemo(() => {
    const subById = {}, roleById = {}, orderIdx = {}, labelById = {};
    lines.forEach((l,i) => { if (l.subcategory) subById[l.id] = l.subcategory; if (l.role) roleById[l.id] = l.role; if (l.label) labelById[l.id] = l.label; orderIdx[l.id] = i; });
    const groupOf = id => {
      const sub = subById[id];
      if (sub && SUBCAT_TO_GROUP[sub]) return SUBCAT_TO_GROUP[sub];
      if (id === 'total_clearing') return 'Cash (CFS/Sweep)';
      if (/^deferred_rev/.test(id)) return 'Deferred Revenue';
      return 'Other';
    };
    const ids = Object.keys(assumptions).sort((a,b)=>(orderIdx[a]??9999)-(orderIdx[b]??9999)||a.localeCompare(b));
    const byTitle = {}, locked = new Set();
    ids.forEach(id => {
      const t = groupOf(id); (byTitle[t] = byTitle[t] || []).push(id);
      const drv = assumptions[id]?.driver, sub = subById[id], role = roleById[id];
      if (LOCKED_DRIVERS.has(drv) || (sub && LOCKED_SUBCATS.has(sub)) || (role && LOCKED_ROLES.has(role)) || id === 'total_clearing') locked.add(id);
    });
    const groups = ASSUM_GROUP_ORDER.filter(t => byTitle[t]?.length).map(t => ({ title: t, ids: byTitle[t] }));
    return { assumGroups: groups, lockedAssumIds: locked, labelById };
  }, [lines, assumptions]);

  // Compute historical days outstanding for DSO/DPO lines dynamically from actuals
  const computeHistDays = useCallback((id) => {
    const am = actualsMonths;
    if (am.length === 0) return {months:[],days:[],avg:0,avg3:0,avg6:0,avg12:0,min:0,max:0};
    const days = am.map(m => {
      const bal = r2(A[id]?.[m] || 0);
      const assum = assumptions[id];
      // Phase 6: base_is_line_id picks any IS line as the divisor. Falls
      // back to the pre-Phase-6 default (DSO→total_revenue, DPO→vendor_opex)
      // when not set. Reads from isData (computed IS) for the historical
      // window, which mirrors what computeBS does at engine time via IS_REF.
      const baseKey = assum?.base_is_line_id || (assum?.driver === 'dpo' ? 'vendor_opex' : 'total_revenue');
      let base;
      if (baseKey === 'vendor_opex') base = VENDOR_OPEX[m] || 1;
      else if (baseKey === 'total_revenue') base = IS_REF_REVENUE[m] || 1;
      else base = isData?.[baseKey]?.[m] || 1;
      return r2(bal / base * 30);
    });
    const valid = days.filter(d => d !== 0 && isFinite(d));
    const last3 = valid.slice(-3); const avg3 = last3.length ? r2(last3.reduce((s,v)=>s+v,0)/last3.length) : 0;
    const last6 = valid.slice(-6); const avg6 = last6.length ? r2(last6.reduce((s,v)=>s+v,0)/last6.length) : 0;
    // Display window: last 12 months
    const last12m = am.slice(-12);
    const last12d = days.slice(-12);
    const displayed = last12d.filter(d => d !== 0 && isFinite(d));
    const avg12 = displayed.length ? r2(displayed.reduce((s,v)=>s+v,0)/displayed.length) : 0;
    const avg = avg12;
    const min = displayed.length ? r2(Math.min(...displayed)) : 0;
    const max = displayed.length ? r2(Math.max(...displayed)) : 0;
    return {months:last12m, days:last12d, avg, avg3, avg6, avg12, min, max};
  },[actualsMonths, assumptions]);

  const renderRow=line=>{
    if(isHidden(line.id))return null;
    const isH=line.type==="header",isT=line.type==="total",isCk=line.type==="check";
    const indent=isH?line.level*16:(line.level*16)+8;
    const keyL=new Set(["total_current_assets","total_fixed_assets","total_intangible_assets","total_assets","total_current_liabilities","total_lt_liabilities","total_liabilities","total_equity","total_le","check"]);
    const isKey=keyL.has(line.id);const isMaj=new Set(["total_assets","total_liabilities","total_equity","total_le"]).has(line.id);
    let cPid=isT&&totalToHeader[line.id]&&collapsed.has(totalToHeader[line.id])?totalToHeader[line.id]:null;
    const mH=isT?totalToHeader[line.id]||null:null;const isClick=isH||(isT&&mH);
    const bg=isCk?"#FFFEF5":isMaj?"#F5F6FA":isKey?"#F7F8FC":isH&&line.level<=1?"#FAFBFE":"#fff";
    const bt=isMaj?"2px solid #C5CAD9":isKey?"1.5px solid #E2E5F0":"none";
    const bb=isCk?"1px solid #E8E4C8":isMaj?"1px solid #C5CAD9":isKey?"1px solid #E2E5F0":"none";
    const ls={...SL,padding:`5px 12px 5px ${12+indent}px`,fontSize:isMaj?11.5:11,fontWeight:isT||isH?700:400,color:isCk?"#B8860B":isMaj?BLACK:isH?"#4B5563":"#374151",background:bg,whiteSpace:"nowrap",cursor:isClick?"pointer":"default",borderTop:bt,borderBottom:bb,minWidth:260};
    const vs=col=>({padding:"5px 6px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontSize:10,fontWeight:isT?700:400,color:isCk?"#B8860B":colColor(col),background:bg,whiteSpace:"nowrap",borderTop:bt,borderBottom:bb,borderRight:isYearEnd(col)?"2px solid rgba(61,133,198,0.15)":"none"});
    const hc=()=>{if(isH)toggle(line.id);else if(isT&&mH)toggle(mH);};
    if(isCk)return (<tr key={line.id}><td style={ls}>{line.label}</td>{columns.map(c=>{const v=bsVal(line.id,c.months);return <td key={c.key}style={vs(c)}>{v===0?"\u2713":fmtK(v)}</td>;})}</tr>);
    return (<tr key={line.id}><td style={ls}onClick={isClick?hc:undefined}>{isH&&arrow(collapsed.has(line.id))}{isT&&cPid&&arrow(true)}{line.label}</td>{columns.map(c=><td key={c.key}style={vs(c)}>{isH?"":fmtK(bsVal(line.id,c.months))}</td>)}</tr>);
  };

  const panelW=380;

  // ★ Item-5 READER — whole-model empty state before the loading/spinner guard.
  /* ★ CRASHROOT PART 2 — these two returns read `modelState`, the provider's single
     terminal-exit answer, instead of inferring the model's state from onboardingGap
     alone. 'empty' REUSES the existing OnboardingIncomplete JSX unchanged (the gap
     object is still what names it); 'error' is the state that previously had NO
     reader at all — a thrown fetch cleared `loading` with allMonths===[] and this
     page spun forever (reproduced pre-fix in diffs/_crashroot-verifyA-pre.txt).
     ⛔ The `!allMonths.length` guard below STAYS, and stays BELOW these returns:
        belt AND braces, and above them it would send a named-empty tenant back to a
        spinner (diffs/crashroot-phase0.txt §4). */
  if (modelState === 'error') return <ModelLoadError message={modelError} />;
  if (modelState === 'empty') return <OnboardingIncomplete gap={onboardingGap} />;
  if (modelState === 'loading' || loading || !allMonths.length) return <div style={{padding:"60px",textAlign:"center",color:"#8B8FA3",fontFamily:"'DM Sans',sans-serif"}}>Loading balance sheet...</div>;

  return (
    <div style={{fontFamily:FONT,background:"#F0F1F5",height:"100%",display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Space+Mono:wght@400;700&display=swap"rel="stylesheet"/>
      <div style={{flexShrink:0,padding:"24px 24px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        {/* THE LENGTH CHECK COMES FIRST — the KPI treatment (b96e9d5), applied to the sibling
    this line was copy-pasted from. It used to open with getYear(...) and only then test
    the RESULT for null, which is a guard on the call that throws, i.e. no guard at all.
    The page gate above already stops an empty window reaching here; the line is now
    correct ON ITS OWN so it does not depend on a guard hundreds of lines away. Same
    output for a real window. */}
        <div><h1 style={{margin:0,fontSize:22,fontWeight:700,color:BLACK,letterSpacing:"-0.5px"}}>Balance Sheet</h1><p style={{margin:"4px 0 0",fontSize:12,color:"#8B8FA3"}}>{companyName||"—"} &mdash; Monthly BS &bull; {(()=>{if(!allMonths.length)return"—";const a=getYear(allMonths[0]);const b=getYear(allMonths[allMonths.length-1]);return a==null?"—":a===b?String(a):`${a}–${b}`;})()}</p></div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <button onClick={()=>setShowPanel(!showPanel)} style={{padding:"6px 14px",borderRadius:6,border:"1px solid "+(showPanel?"#3D5AF1":"#D1D5DB"),background:showPanel?"#E8EAFF":"#fff",fontSize:11,fontWeight:600,cursor:"pointer",color:showPanel?"#3D5AF1":"#4B5563"}}>{showPanel?"Hide Forecast Assumptions":"Forecast Assumptions"}</button>
          <div style={{display:"flex",borderRadius:6,border:"1px solid #D1D5DB",overflow:"hidden"}}>
            {[["monthly","Monthly"],["quarterly","Quarterly"],["annual","Annual"]].map(([v,l])=>(<button key={v}onClick={()=>setViewMode(v)}style={{padding:"6px 14px",fontSize:11,fontWeight:600,cursor:"pointer",border:"none",background:viewMode===v?"#E8EAFF":"#fff",color:viewMode===v?"#3D5AF1":"#4B5563",borderRight:v!=="annual"?"1px solid #D1D5DB":"none"}}>{l}</button>))}
          </div>
          <div style={{width:1,height:24,background:"#E2E5F0"}}/>
          <div style={{display:"flex",alignItems:"center",gap:6,padding:"5px 12px",borderRadius:6,border:"1px solid #D1D5DB",background:"#fff"}}>
            <span style={{fontSize:10,fontWeight:600,color:"#6B7280",whiteSpace:"nowrap"}}>Last Closed Month</span>
            {/* READOUT, not a control. The dropdown offered all 36 window months:
                27 re-sliced the actuals/forecast seam and 9 (every forecast month)
                made runFullModel throw. The close month is owned by the data, and
                the Monthly Close module will own advancing it. */}
            <span style={{fontSize:11,fontWeight:700,color:BLACK,padding:"2px 4px"}}>{formatMonthLabel(cutoff, "shortFull")}</span>
          </div>
          <div style={{width:1,height:24,background:"#E2E5F0"}}/>
          <button onClick={()=>setCollapsed(new Set(lines.filter(l=>l.type==="header").map(l=>l.id)))}style={{padding:"6px 14px",borderRadius:6,border:"1px solid #D1D5DB",background:"#fff",fontSize:11,fontWeight:600,cursor:"pointer",color:"#4B5563"}}>Collapse All</button>
          <button onClick={()=>setCollapsed(new Set())}style={{padding:"6px 14px",borderRadius:6,border:"1px solid #D1D5DB",background:"#fff",fontSize:11,fontWeight:600,cursor:"pointer",color:"#4B5563"}}>Expand All</button>
        </div>
      </div>
      <div style={{flex:1,display:"flex",overflow:"hidden",margin:"0 24px"}}>
        {showPanel&&(
          <div style={{width:panelW,flexShrink:0,background:"#fff",borderRadius:"12px 0 0 0",border:"1px solid #E2E5F0",borderRight:"none",overflowY:"auto",padding:"16px"}}>
            <div style={{fontSize:13,fontWeight:700,color:BLACK,marginBottom:2}}>Forecast Assumptions</div>
            {canEditModel?<div data-bs-autosave style={{fontSize:10,color:"#6B7280",marginBottom:8}}>Changes save as you edit.</div>:<div style={{fontSize:10,marginBottom:8}}><ViewOnlyNote/></div>}
            <label style={{display:"flex",alignItems:"center",gap:6,fontSize:10,color:"#6B7280",marginBottom:12,cursor:"pointer"}}>
              <input type="checkbox" checked={showLocked} onChange={e=>setShowLocked(e.target.checked)} style={{cursor:"pointer"}}/>
              Show derived/locked lines
            </label>
            {assumGroups.map(g=>{
              const settable=g.ids.filter(id=>!lockedAssumIds.has(id));
              const visible=showLocked?g.ids:settable;
              if(!visible.length)return null;
              return (
              <div key={g.title} style={{marginBottom:16}}>
                <div style={{fontSize:10,fontWeight:700,color:"#6B7280",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:8,paddingBottom:4,borderBottom:"1px solid #E2E5F0"}}>{g.title}</div>
                {visible.map(id=>{
                  const a=assumptions[id];if(!a)return null;
                  const isFormula=assumptions[id]?.driver==="formula";
                  const lastVal=r2(A[id]?.[cutoff]||0);
                  return (
                    <div key={id}style={{padding:"6px 0",borderBottom:"1px solid #F5F6FA"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                        <span style={{fontSize:11,fontWeight:500,color:BLACK}}>{labelById[id]||a.label||id}</span>
                        {!isFormula&&<span style={{fontSize:9,color:BLUE}}>Last: {fmtK(lastVal)}</span>}
                      </div>
                      {isFormula?(
                        <div style={{fontSize:10,color:"#6B7280",fontStyle:"italic"}}>{a.source||"Formula"}</div>
                      ):a.driver==="module_link"?(
                        <div style={{display:"flex",gap:6,alignItems:"center"}}>
                          <div style={{fontSize:10,padding:"3px 6px",borderRadius:4,border:"1px solid #E2E5F0",background:"#F5F6FA",color:"#6B7280",flex:1,fontStyle:"italic"}}>{a.moduleLabel||a.source}</div>
                          <span style={{fontSize:8,fontWeight:700,color:"#8B8FA3",border:"1px solid #E2E5F0",borderRadius:4,padding:"2px 6px",whiteSpace:"nowrap"}}>{"🔒"} Engine-driven</span>
                        </div>
                      ):lockedAssumIds.has(id)?(
                        <div style={{display:"flex",gap:6,alignItems:"center"}}>
                          <div style={{fontSize:10,padding:"3px 6px",borderRadius:4,border:"1px solid #E2E5F0",background:"#F5F6FA",color:"#6B7280",flex:1,fontStyle:"italic"}}>Sweep / clearing — flexes to sweep target</div>
                          <span style={{fontSize:8,fontWeight:700,color:"#8B8FA3",border:"1px solid #E2E5F0",borderRadius:4,padding:"2px 6px",whiteSpace:"nowrap"}}>{"🔒"} Sweep-driven</span>
                        </div>
                      ):(
                        <div>
                          <div style={{display:"flex",gap:6,alignItems:"center"}}>
                            {/* W-029: every control is captioned, with a title and an aria-label. */}
                            <span style={{fontSize:9,color:"#8B8FA3"}}>Driver</span>
                            <ViewOnly canEdit={canEditModel}><select value={a.driver}onChange={e=>updateDriver(id,e.target.value)} title="Driver -- how this line is forecast" aria-label="Driver" style={{fontSize:10,padding:"3px 4px",borderRadius:4,border:"1px solid #D1D5DB",background:"#fff",flex:1,cursor:"pointer"}}>
                              {DRIVER_OPTS.map(o=><option key={o.v}value={o.v}>{o.l}</option>)}
                            </select></ViewOnly>
                            {(a.driver==="dso"||a.driver==="dpo")&&<span style={{fontSize:9,color:"#8B8FA3"}}>Days</span>}
                            {(a.driver==="dso"||a.driver==="dpo")&&(
                              <ViewOnly canEdit={canEditModel}><input type="number" defaultValue={a.days} key={id+"-days-"+a.days} onBlur={e=>commitDays(id,e.target.value)} step="any" title={a.driver==="dso"?"Days -- days of sales outstanding":"Days -- days of payables outstanding"} aria-label="Days" style={{width:60,fontSize:10,padding:"3px 4px",borderRadius:4,border:"1px solid "+(daysRequired[id]?"#E07A5F":"#D1D5DB"),textAlign:"right"}}/></ViewOnly>
                            )}
                            {(a.driver==="dso"||a.driver==="dpo")&&<button onClick={()=>toggleAssum(id)} title="History -- the actual days in the closed months, and quick picks" aria-label="History" style={{fontSize:9,padding:"2px 6px",borderRadius:4,border:"1px solid #D1D5DB",background:expandedAssum.has(id)?"#E8EAFF":"#fff",cursor:"pointer",color:"#4B5563",whiteSpace:"nowrap"}}>{expandedAssum.has(id)?"History \u25B2":"History \u25BC"}</button>}
                            {a.driver==="assumption"&&<button onClick={()=>toggleAssum(id)}style={{fontSize:9,padding:"2px 8px",borderRadius:4,border:"1px solid #D1D5DB",background:expandedAssum.has(id)?"#E8EAFF":"#fff",cursor:"pointer",color:"#4B5563",whiteSpace:"nowrap"}}>{expandedAssum.has(id)?"Hide":"Edit"} Months</button>}
                          </div>
                          {(a.driver==="dso"||a.driver==="dpo")&&daysRequired[id]&&<div data-bs-days-required style={{fontSize:10,fontWeight:600,color:"#E07A5F",marginTop:3}}>{"Required \u2014 enter the number of days"}</div>}
                          {/* Phase 6: Base IS Line dropdown for DSO/DPO drivers */}
                          {(a.driver==="dso"||a.driver==="dpo")&&(
                            <div style={{display:"flex",gap:6,alignItems:"center",marginTop:4}}>
                              <span style={{fontSize:9,color:"#8B8FA3",minWidth:54}}>Based on:</span>
                              <ViewOnly canEdit={canEditModel}><select value={a.base_is_line_id || (a.driver==="dpo" ? 'vendor_opex' : 'total_revenue')} onChange={e=>updateBase(id,e.target.value)} title="Based on -- the income-statement line the days are measured against" aria-label="Based on" style={{fontSize:10,padding:"2px 4px",borderRadius:4,border:"1px solid #D1D5DB",background:"#fff",flex:1,cursor:"pointer"}}>
                                {baseLineOptions.map(o=><option key={o.id} value={o.id}>{o.label}</option>)}
                              </select></ViewOnly>
                            </div>
                          )}
                          {(a.driver==="dso"||a.driver==="dpo")&&expandedAssum.has(id)&&(()=>{
                            const h=computeHistDays(id);
                            const barMax=h.max||1;
                            return (
                              <div style={{marginTop:6,background:"#FAFBFE",borderRadius:6,padding:8,border:"1px solid #E2E5F0"}}>
                                <div style={{fontSize:9,fontWeight:600,color:"#6B7280",marginBottom:6}}>Historical {a.driver==="dso"?"DSO":"DPO"} (last {h.months.length} actuals months)</div>
                                <div style={{display:"flex",gap:2,alignItems:"flex-end",height:48,marginBottom:4}}>
                                  {h.months.map((m,i)=>{const d=h.days[i];const pct=Math.max(2,d/barMax*100);return (
                                    <div key={m}style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center"}}>
                                      <div style={{fontSize:7,color:"#6B7280",marginBottom:1}}>{fmtDays(d,h.max)}</div>
                                      <div style={{width:"100%",height:pct*0.4,background:d===h.max?"#EF4444":d===h.min?"#22C55E":"#93C5FD",borderRadius:2,minHeight:2}}/>
                                    </div>
                                  );})}
                                </div>
                                <div style={{display:"flex",gap:2,marginBottom:8}}>
                                  {h.months.map(m=> <div key={m}style={{flex:1,textAlign:"center",fontSize:7,color:"#8B8FA3"}}>{formatMonthLabel(m, "short")}</div>)}
                                </div>
                                <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:6,fontSize:9,color:"#374151"}}>
                                  <span>Min: <b style={{color:"#22C55E"}}>{fmtDays(h.min,h.max)}</b></span>
                                  <span style={{color:"#D1D5DB"}}>|</span>
                                  <span>Max: <b style={{color:"#EF4444"}}>{fmtDays(h.max,h.max)}</b></span>
                                  <span style={{color:"#D1D5DB"}}>|</span>
                                  <span>Avg: <b>{fmtDays(h.avg,h.max)}</b></span>
                                </div>
                                <ViewOnly canEdit={canEditModel}><div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                                  {[{label:"Last 3mo",val:h.avg3},{label:"Last 6mo",val:h.avg6},{label:"Last 12mo",val:h.avg12},{label:"Min",val:h.min},{label:"Max",val:h.max}].map(b=> (
                                    <button key={b.label} onClick={()=>updateDays(id,b.val)} style={{fontSize:8,padding:"3px 6px",borderRadius:4,border:"1px solid #D1D5DB",background:r2(a.days)===r2(b.val)?"#E8EAFF":"#fff",cursor:"pointer",color:"#374151",whiteSpace:"nowrap"}}>
                                      {b.label}: <b>{fmtDays(b.val,h.max)}</b>
                                    </button>
                                  ))}
                                </div></ViewOnly>
                              </div>
                            );
                          })()}
                          {a.driver==="assumption"&&a.source&&<div style={{fontSize:9,color:"#8B8FA3",marginTop:2,fontStyle:"italic"}}>{a.source}</div>}
                          {a.driver==="assumption"&&expandedAssum.has(id)&&(
                            <ViewOnly canEdit={canEditModel}><div style={{marginTop:6,background:"#FAFBFE",borderRadius:6,padding:8,border:"1px solid #E2E5F0"}}>
                              <div style={{display:"flex",gap:4,alignItems:"center",marginBottom:6}}>
                                <span style={{fontSize:9,color:"#6B7280"}}>Apply to all:</span>
                                <input type="number" step="any" placeholder="value" onKeyDown={e=>{if(e.key==="Enter")applyToAll(id,e.target.value)}} style={{width:80,fontSize:9,padding:"2px 4px",borderRadius:3,border:"1px solid #D1D5DB",textAlign:"right"}}/>
                                <span style={{fontSize:8,color:"#8B8FA3"}}>Enter to apply</span>
                              </div>
                              <div style={{maxHeight:160,overflowY:"auto"}}>
                                {forecastMonths.map(m=>{const cv=a.values?.[m]!==undefined?a.values[m]:(H[id]?.[m]||0);return (
                                  <div key={m}style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"2px 0"}}>
                                    <span style={{fontSize:9,color:"#374151",fontWeight:500}}>{formatMonthLabel(m, "short")} {m.split("-")[0]}</span>
                                    <input type="number" defaultValue={cv} key={cv+"-"+m} onBlur={e=>{ /* Batch 1b (2026-11-03, L3653): a blank (or non-number) means "the default" -- show it at once; it used to stay blank until the next re-key. updateAssumValue gets the typed text, unchanged. */ const t=e.target.value; if(String(t).trim()===""||!Number.isFinite(parseFloat(t))) e.target.value=String(H[id]?.[m]||0); updateAssumValue(id,m,t); }} step="any" style={{width:90,fontSize:9,padding:"2px 4px",borderRadius:3,border:"1px solid #D1D5DB",textAlign:"right",fontFamily:"'Space Mono',monospace"}}/>
                                  </div>
                                );})}
                              </div>
                            </div></ViewOnly>
                          )}
                        </div>
                      )}
                      {saveErr[id]&&<div data-bs-save-error style={{fontSize:10,fontWeight:600,color:"#E07A5F",marginTop:3}}>{saveErr[id]}</div>}
                    </div>
                  );
                })}
              </div>
              );
            })}
          </div>
        )}
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
          <style>{`.bs-tw::-webkit-scrollbar{width:8px;height:8px}.bs-tw::-webkit-scrollbar-thumb{background:#C5CAD9;border-radius:4px}`}</style>
          <div className="bs-tw"style={{flex:1,background:"#fff",borderRadius:showPanel?"0 12px 12px 0":"12px",border:"1px solid #E2E5F0",overflow:"auto",minHeight:0}}>
            <table style={{borderCollapse:"separate",borderSpacing:0,width:"max-content",minWidth:"100%"}}>
              <thead style={{position:"sticky",top:0,zIndex:10}}>
                <tr style={{background:"#FAFBFE"}}>
                  <th style={{...SL,padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"#A0A3B8",background:"#FAFBFE",zIndex:12}}></th>
                  {yearBands.map((b,i)=>{const y=b.year;const fM=b.months[0],lM=b.months[b.months.length-1];const aA=lM<=cutoff;const aF=fM>cutoff;const lb=aA?"ACTUALS":aF?"FORECAST":"ACTUALS / FORECAST";const co=aF?"#3D85C6":BLUE;const cn=columns.filter(c=>c.year===y).length;return (<th key={y}colSpan={cn}style={{padding:"6px",textAlign:"center",fontSize:10,fontWeight:700,color:co,background:"#FAFBFE",borderRight:i!==yearBands.length-1?"2px solid rgba(61,133,198,0.15)":"none"}}>{y} &mdash; {lb}</th>);})}
                </tr>
                <tr style={{background:"#fff",borderBottom:"2px solid #E2E5F0"}}>
                  <th style={{...SL,padding:"8px 12px",textAlign:"left",fontSize:9,fontWeight:600,color:"#8B8FA3",minWidth:260,background:"#fff",zIndex:12}}>Line Item</th>
                  {columns.map(c=>(<th key={c.key}style={{padding:"8px 4px",textAlign:"center",fontSize:9,fontWeight:600,color:colColor(c),whiteSpace:"nowrap",minWidth:viewMode==="monthly"?78:viewMode==="quarterly"?95:115,borderRight:isYearEnd(c)?"2px solid rgba(61,133,198,0.15)":"none"}}>{c.label}</th>))}
                </tr>
              </thead>
              <tbody>{lines.map(l=>renderRow(l))}</tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

